import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, MapPin, X, Check } from "lucide-react";

const EMPTY_ADDRESS = {
  label: "Home",
  name: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  pincode: "",
  is_default: false,
};

function AddressFormModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY_ADDRESS);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const valid =
    form.name.trim() &&
    /^[0-9]{10}$/.test(form.phone.trim()) &&
    form.line1.trim() &&
    form.city.trim() &&
    form.state.trim() &&
    /^[0-9]{6}$/.test(form.pincode.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please fill all required fields correctly.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/addresses/${initial.id}`, form);
        onSaved(data, "updated");
      } else {
        const { data } = await api.post("/addresses", form);
        onSaved(data, "created");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save address");
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-6" onClick={onClose}>
      <div
        className="bg-background w-full sm:max-w-lg max-h-[92vh] overflow-y-auto border border-border"
        onClick={(e) => e.stopPropagation()}
        data-testid="address-form-modal"
      >
        <div className="flex items-center justify-between px-6 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-border sticky top-0 bg-background">
          <h3 className="font-serif text-2xl">{isEdit ? "Edit address" : "Add a new address"}</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-foreground" data-testid="address-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 sm:px-8 py-6 space-y-5">
          <div className="flex gap-2">
            {["Home", "Work", "Other"].map((l) => (
              <button
                type="button"
                key={l}
                onClick={() => setForm((f) => ({ ...f, label: l }))}
                className={`ie-overline px-4 py-2 border ${form.label === l ? "border-brand-accent text-brand-accent" : "border-border text-text-secondary"}`}
                data-testid={`address-label-${l.toLowerCase()}`}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name *" value={form.name} onChange={set("name")} testid="address-name" />
            <Field label="Phone (10 digits) *" value={form.phone} onChange={set("phone")} maxLength={10} testid="address-phone" />
          </div>

          <Field label="Address line 1 *" value={form.line1} onChange={set("line1")} testid="address-line1" />
          <Field label="Address line 2" value={form.line2} onChange={set("line2")} testid="address-line2" />

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="City *" value={form.city} onChange={set("city")} testid="address-city" />
            <Field label="State *" value={form.state} onChange={set("state")} testid="address-state" />
            <Field label="Pincode *" value={form.pincode} onChange={set("pincode")} maxLength={6} testid="address-pincode" />
          </div>

          <label className="flex items-center gap-3 text-sm text-text-secondary cursor-pointer pt-1">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
              className="w-4 h-4 accent-current"
              data-testid="address-set-default-checkbox"
            />
            Set as default address
          </label>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="ie-btn-primary flex-1 disabled:opacity-60" data-testid="address-form-submit">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Save address"}
            </button>
            <button type="button" onClick={onClose} className="ie-btn-ghost" data-testid="address-form-cancel">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MessageModal({
  open,
  title,
  message,
  inputLabel,
  inputPlaceholder,
  inputValue,
  onInputChange,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onClose,
  loading = false,
  type = "default",
}) {
  if (!open) return null;

  const iconClass =
    type === "error"
      ? "text-red-500"
      : type === "success"
        ? "text-brand-accent"
        : "text-text-secondary";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-5"
      onClick={onClose}
      data-testid="message-modal-overlay"
    >
      <div
        className="bg-background w-full max-w-md border border-border shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="message-modal"
      >
        <div className="flex items-start justify-between gap-4 px-6 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-border">
          <div>
            <h3 className="font-serif text-2xl">{title}</h3>
            {message && (
              <p className={`text-sm ${iconClass} mt-2 leading-relaxed`}>
                {message}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="text-text-secondary hover:text-foreground disabled:opacity-50"
            aria-label="Close"
            data-testid="message-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 sm:px-8 py-6">
          {inputLabel && (
            <label className="block">
              <span className="ie-overline text-text-secondary block mb-2">
                {inputLabel}
              </span>

              <textarea
                value={inputValue || ""}
                onChange={(e) => onInputChange?.(e.target.value)}
                placeholder={inputPlaceholder || ""}
                rows={4}
                className="w-full border border-border bg-background px-4 py-3 text-sm resize-none focus:outline-none focus:border-brand-accent"
                disabled={loading}
                data-testid="message-modal-input"
              />
            </label>
          )}

          <div className="flex gap-3 pt-5">
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="ie-btn-primary flex-1 disabled:opacity-60"
              data-testid="message-modal-confirm"
            >
              {loading ? "Please wait…" : confirmText}
            </button>

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="ie-btn-ghost"
              data-testid="message-modal-cancel"
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReplacementModal({
  open,
  order,
  itemIndex,
  setItemIndex,
  quantity,
  setQuantity,
  reason,
  setReason,
  loading,
  onConfirm,
  onClose,
}) {
  if (!open || !order) return null;

  const selectedItem = order.items?.[itemIndex];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-5"
      onClick={loading ? undefined : onClose}
    >
      <div
        className="bg-background w-full max-w-md border border-border shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-6 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-border">
          <div>
            <h3 className="font-serif text-2xl">
              Request Replacement
            </h3>

            <p className="text-sm text-text-secondary mt-2">
              Select the item you want to replace.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="text-text-secondary hover:text-foreground disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 sm:px-8 py-6 space-y-5">

          {/* Item */}
          <label className="block">
            <span className="ie-overline text-text-secondary block mb-2">
              Item
            </span>

            <select
              value={itemIndex}
              onChange={(e) => {
                setItemIndex(Number(e.target.value));
                setQuantity(1);
              }}
              disabled={loading}
              className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:border-brand-accent"
            >
              {order.items?.map((item, index) => (
                <option key={index} value={index}>
                  {item.name}
                  {item.size ? ` — Size ${item.size}` : ""}
                  {item.color ? ` — ${item.color}` : ""}
                </option>
              ))}
            </select>
          </label>

          {/* Quantity */}
          {selectedItem && (
            <label className="block">
              <span className="ie-overline text-text-secondary block mb-2">
                Quantity
              </span>

              <select
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                disabled={loading}
                className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:border-brand-accent"
              >
                {Array.from(
                  { length: selectedItem.qty },
                  (_, index) => index + 1
                ).map((qty) => (
                  <option key={qty} value={qty}>
                    {qty}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Reason */}
          <label className="block">
            <span className="ie-overline text-text-secondary block mb-2">
              Reason for replacement
            </span>

            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Tell us why you want to replace this item..."
              rows={4}
              disabled={loading}
              className="w-full border border-border bg-background px-4 py-3 text-sm resize-none focus:outline-none focus:border-brand-accent"
            />
          </label>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="ie-btn-primary flex-1 disabled:opacity-60"
            >
              {loading ? "Processing…" : "Request Replacement"}
            </button>

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="ie-btn-ghost"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="ie-overline text-text-secondary block mb-2">{label}</span>
      <input
        {...props}
        className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:border-brand-accent"
        data-testid={testid}
      />
    </label>
  );
}

function AddressCard({ addr, onEdit, onDelete, onSetDefault }) {
  return (
    <div
      className={`border p-5 sm:p-6 relative ${addr.is_default ? "border-brand-accent" : "border-border"} bg-surface`}
      data-testid={`address-card-${addr.id}`}
    >
      {addr.is_default && (
        <span className="absolute -top-3 left-5 bg-brand-accent text-background ie-overline px-3 py-1" data-testid={`address-default-badge-${addr.id}`}>
          Default
        </span>
      )}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-brand-accent flex-shrink-0" />
          <span className="ie-overline text-brand-accent">{addr.label}</span>
        </div>
        <div className="flex gap-3 flex-shrink-0">
          <button onClick={() => onEdit(addr)} className="text-text-secondary hover:text-brand-accent" data-testid={`address-edit-${addr.id}`} aria-label="Edit address">
            <Pencil className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(addr.id)} className="text-text-secondary hover:text-red-500" data-testid={`address-delete-${addr.id}`} aria-label="Delete address">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="font-serif text-lg mb-1">{addr.name}</div>
      <div className="text-sm text-text-secondary leading-relaxed">
        {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />
        {addr.city}, {addr.state} – {addr.pincode}<br />
        Phone: {addr.phone}
      </div>

      {!addr.is_default && (
        <button
          onClick={() => onSetDefault(addr.id)}
          className="ie-overline text-text-secondary hover:text-brand-accent mt-4 inline-flex items-center gap-2"
          data-testid={`address-set-default-${addr.id}`}
        >
          <Check className="w-3.5 h-3.5" /> Set as default
        </button>
      )}
    </div>
  );
}

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { ids: wishlistIds } = useWishlist();
  const [savedProducts, setSavedProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState(
  "Customer requested cancellation"
  );
  const [cancelLoading, setCancelLoading] = useState(false);
  const [replacementModalOpen, setReplacementModalOpen] = useState(false);
  const [replacementOrder, setReplacementOrder] = useState(null);
  const [replacementItemIndex, setReplacementItemIndex] = useState(0);
  const [replacementQuantity, setReplacementQuantity] = useState(1);
  const [replacementReason, setReplacementReason] = useState("");
  const [replacementLoading, setReplacementLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  useEffect(() => {
    if (wishlistIds.length === 0) { setSavedProducts([]); return; }
    api.get("/products", { params: { limit: 60 } }).then(({ data }) => {
      setSavedProducts((data.items || []).filter((p) => wishlistIds.includes(p.id)));
    });
  }, [wishlistIds]);

  useEffect(() => {
    setOrdersLoading(true);
    api.get("/orders")
      .then(({ data }) => setOrders(data || []))
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  }, []);

  const loadAddresses = () => {
    setAddressesLoading(true);
    api.get("/addresses")
      .then(({ data }) => setAddresses(data || []))
      .catch(() => setAddresses([]))
      .finally(() => setAddressesLoading(false));
  };

  useEffect(() => { loadAddresses(); }, []);

  const openAdd = () => { setEditingAddress(null); setModalOpen(true); };
  const openEdit = (addr) => { setEditingAddress(addr); setModalOpen(true); };

  const handleSaved = (addr, mode) => {
    setModalOpen(false);
    toast.success(mode === "created" ? "Address added" : "Address updated");
    loadAddresses();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this address?")) return;
    try {
      await api.delete(`/addresses/${id}`);
      toast.success("Address removed");
      loadAddresses();
    } catch {
      toast.error("Could not remove address");
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await api.post(`/addresses/${id}/default`);
      loadAddresses();
    } catch {
      toast.error("Could not set default address");
    }
  };
  const handleCancelOrder = (orderId) => {
   setCancelOrderId(orderId);
   setCancelReason("Customer requested cancellation");
   setCancelModalOpen(true);
  };
  const confirmCancelOrder = async () => {
  if (!cancelOrderId) return;

  setCancelLoading(true);

  try {
    await api.post(`/orders/${cancelOrderId}/cancel`, {
      reason: cancelReason.trim() || "Customer requested cancellation",
    });

    setCancelModalOpen(false);
    setCancelOrderId(null);
    setCancelReason("Customer requested cancellation");

    toast.success("Order cancelled successfully.");

    const { data } = await api.get("/orders");
    setOrders(data || []);
  } catch (err) {
    setCancelModalOpen(false);

    toast.error(
      err?.response?.data?.detail ||
      "Unable to cancel the order."
    );
  } finally {
    setCancelLoading(false);
  }
};

const openReplacementModal = (order) => {
  setReplacementOrder(order);
  setReplacementItemIndex(0);
  setReplacementQuantity(1);
  setReplacementReason("");
  setReplacementModalOpen(true);
};

const submitReplacementRequest = async () => {
  if (!replacementOrder) return;

  if (!replacementReason.trim()) {
    toast.error("Please provide a reason for replacement.");
    return;
  }

  const selectedItem = replacementOrder.items?.[replacementItemIndex];

  if (!selectedItem) {
    toast.error("Please select an item.");
    return;
  }

  if (replacementQuantity < 1 || replacementQuantity > selectedItem.qty) {
    toast.error("Invalid replacement quantity.");
    return;
  }

  setReplacementLoading(true);

  try {
    await api.post(
      `/orders/${replacementOrder.order_id}/service-request`,
      {
        request_type: "replacement",
        reason: replacementReason.trim(),
        item_index: replacementItemIndex,
        quantity: replacementQuantity,
      }
    );

    setReplacementModalOpen(false);
    setReplacementOrder(null);
    setReplacementReason("");

    toast.success(
      "Replacement request created. Return pickup has been initiated."
    );

    const { data } = await api.get("/orders");
    setOrders(data || []);
  } catch (err) {
    toast.error(
      err?.response?.data?.detail ||
      "Unable to create replacement request."
    );
  } finally {
    setReplacementLoading(false);
  }
};

  if (!user) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-20" data-testid="profile-page">
      <div className="flex items-end justify-between mb-10 md:mb-12 flex-wrap gap-4">
        <div>
          <div className="ie-overline text-brand-accent mb-3">Atelier Account</div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05]">Welcome, {user.name.split(" ")[0]}.</h1>
          <div className="text-sm text-text-secondary mt-3">{user.email}</div>
        </div>
        <button onClick={() => { logout(); navigate("/"); toast("Signed out"); }} className="ie-btn-ghost" data-testid="profile-logout">
          Sign Out
        </button>
      </div>

      <Tabs defaultValue="orders" className="w-full">
        <TabsList className="rounded-none bg-transparent h-auto p-0 border-b border-border w-full justify-start gap-5 sm:gap-8 overflow-x-auto">
          {[
            ["orders", "Orders"],
            ["saved", "Saved"],
            ["addresses", "Addresses"],
            ["settings", "Settings"],
          ].map(([k, l]) => (
            <TabsTrigger key={k} value={k} className="rounded-none px-0 pb-4 ie-overline data-[state=active]:bg-transparent data-[state=active]:text-brand-accent data-[state=active]:border-b-2 data-[state=active]:border-brand-accent flex-shrink-0" data-testid={`profile-tab-${k}`}>
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ORDERS */}
        <TabsContent value="orders" className="pt-8 md:pt-10" data-testid="orders-tab-content">
          {ordersLoading ? (
            <div className="ie-overline text-text-secondary py-10">Loading orders…</div>
          ) : orders.length === 0 ? (
            <div className="border border-border bg-surface p-8 sm:p-10 text-center">
              <Package className="w-8 h-8 text-text-secondary mx-auto mb-4"/>
              <div className="ie-overline text-text-secondary mb-3">No orders yet</div>
              <h3 className="font-serif text-2xl sm:text-3xl mb-3">A quiet beginning awaits.</h3>
              <p className="text-sm text-text-secondary mb-8">Your atelier orders will appear here.</p>
              <Link to="/shop" className="ie-btn-primary inline-flex">Browse the Atelier</Link>
            </div>
          ) : (
            <ul className="space-y-5" data-testid="orders-list">
              {orders.map((o) => (
                <li key={o.order_id} className="border border-border bg-surface p-5 sm:p-7" data-testid={`order-row-${o.order_id}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
                    <div>
                      <div className="ie-overline text-brand-accent">Order</div>
                      <div className="font-serif text-xl sm:text-2xl">{o.order_id}</div>
                    </div>
                    <div className="text-right">
                      <div className="ie-overline text-text-secondary">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
                      <div className="ie-overline text-brand-accent mt-1">{o.status.replace(/-/g, " ")}</div>
                      {o.courier_name && (
                        <div className="text-[10px] text-text-secondary mt-1" data-testid={`order-courier-${o.order_id}`}>{o.courier_name}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 overflow-x-auto pb-1 mb-4">
                    {o.items.slice(0, 4).map((it, i) => (
                      <div key={i} className="w-14 h-16 bg-background flex-shrink-0 overflow-hidden">
                        {it.image && <img src={it.image} alt="" className="w-full h-full object-cover"/>}
                      </div>
                    ))}
                    {o.items.length > 4 && (
                      <div className="w-14 h-16 bg-background flex-shrink-0 flex items-center justify-center text-xs text-text-secondary">
                        +{o.items.length - 4}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3">
  <div>
    <div className="ie-overline text-text-secondary">Total</div>
    <div className="font-serif text-xl">
      ₹{o.total.toLocaleString("en-IN")}
    </div>
  </div>

  <div className="flex flex-col items-start gap-3">

  {/* Track Order */}
  <Link
    to={`/track?id=${o.order_id}&email=${encodeURIComponent(o.email)}`}
    className="ie-overline hover:text-brand-accent inline-flex items-center gap-2"
    data-testid={`track-order-link-${o.order_id}`}
  >
    Track this order →
  </Link>

  {/* Cancel Order */}
  {["placed", "preparing"].includes(o.status) && (
    <button
      type="button"
      onClick={() => handleCancelOrder(o.order_id)}
      className="ie-overline text-red-500 hover:text-red-600"
      data-testid={`cancel-order-${o.order_id}`}
    >
      Cancel Order
    </button>
  )}

  {/* Replacement */}
  {(() => {
    if (o.status !== "delivered" || !o.delivered_at) {
      return null;
    }

    const deliveredAt = new Date(o.delivered_at);

    if (Number.isNaN(deliveredAt.getTime())) {
      return null;
    }

    const replacementDeadline = new Date(deliveredAt);
    replacementDeadline.setDate(
      replacementDeadline.getDate() + 7
    );

    const replacementWindowOpen =
      new Date() <= replacementDeadline;

    const hasActiveReplacement = (o.service_requests || []).some(
      (sr) =>
        sr.request_type === "replacement" &&
        [
          "requested",
          "return_pickup_created",
          "return_in_transit",
          "return_delivered",
          "replacement_shipment_created",
          "replacement_in_transit",
        ].includes(sr.status)
    );

    if (hasActiveReplacement) {
      return (
        <span
          className="ie-overline text-text-secondary"
          data-testid={`replacement-status-${o.order_id}`}
        >
          Replacement in progress
        </span>
      );
    }

    if (!replacementWindowOpen) {
      return (
        <span
          className="ie-overline text-text-secondary"
          data-testid={`replacement-expired-${o.order_id}`}
        >
          Replacement window expired
        </span>
      );
    }

    return (
      <button
        type="button"
        onClick={() => openReplacementModal(o)}
        className="ie-overline text-brand-accent hover:underline"
        data-testid={`replacement-order-${o.order_id}`}
      >
        Request Replacement
      </button>
    );
  })()}

</div>
</div>            
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        {/* SAVED */}
        <TabsContent value="saved" className="pt-8 md:pt-10" data-testid="saved-tab-content">
          {savedProducts.length === 0 ? (
            <div className="border border-border bg-surface p-8 sm:p-10 text-center">
              <div className="ie-overline text-text-secondary mb-3">Wishlist is empty</div>
              <h3 className="font-serif text-2xl sm:text-3xl mb-3">Nothing saved · yet.</h3>
              <Link to="/shop" className="ie-btn-primary inline-flex mt-4">Discover Pieces</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {savedProducts.map((p) => <ProductCard key={p.id} product={p}/>)}
            </div>
          )}
        </TabsContent>

        {/* ADDRESSES */}
        <TabsContent value="addresses" className="pt-8 md:pt-10" data-testid="addresses-tab-content">
          <div className="flex items-center justify-between mb-6">
            <div className="ie-overline text-text-secondary">
              {addressesLoading ? "Loading…" : `${addresses.length} saved address${addresses.length === 1 ? "" : "es"}`}
            </div>
            <button onClick={openAdd} className="ie-btn-primary inline-flex items-center gap-2" data-testid="add-address-btn">
              <Plus className="w-4 h-4" /> Add new address
            </button>
          </div>

          {addressesLoading ? (
            <div className="ie-overline text-text-secondary py-10">Loading addresses…</div>
          ) : addresses.length === 0 ? (
            <div className="border border-border bg-surface p-8 sm:p-10 text-center">
              <MapPin className="w-8 h-8 text-text-secondary mx-auto mb-4" />
              <div className="ie-overline text-text-secondary mb-3">No addresses on file</div>
              <h3 className="font-serif text-xl sm:text-2xl mb-3">Save an address for faster checkout.</h3>
              <p className="text-sm text-text-secondary mb-6">Add as many delivery addresses as you like and pick one in a tap at checkout.</p>
              <button onClick={openAdd} className="ie-btn-primary inline-flex items-center gap-2 mx-auto" data-testid="add-first-address-btn">
                <Plus className="w-4 h-4" /> Add your first address
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5 sm:gap-6" data-testid="addresses-list">
              {addresses.map((addr) => (
                <AddressCard
                  key={addr.id}
                  addr={addr}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onSetDefault={handleSetDefault}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* SETTINGS */}
        <TabsContent value="settings" className="pt-8 md:pt-10" data-testid="settings-tab-content">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="border border-border bg-surface p-6 sm:p-8">
              <div className="ie-overline mb-4">Account</div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between gap-4"><span className="text-text-secondary">Name</span><span className="font-serif text-right">{user.name}</span></div>
                  <div className="flex justify-between gap-4"><span className="text-text-secondary">Mobile</span><span className="font-serif text-right">{user.phone}</span></div>
                <div className="flex justify-between gap-4"><span className="text-text-secondary">Email</span><span className="font-serif text-right truncate">{user.email}</span></div>
                <div className="flex justify-between gap-4"><span className="text-text-secondary">Role</span><span className="ie-overline">{user.role}</span></div>
                <div className="flex justify-between gap-4"><span className="text-text-secondary">Verified</span><span className="ie-overline text-brand-accent">{user.is_verified ? "Yes" : "Pending"}</span></div>
              </div>
            </div>
            <div className="border border-border bg-surface p-6 sm:p-8">
              <div className="ie-overline mb-4">Communication</div>
              <p className="text-sm text-text-secondary leading-relaxed">
                You're subscribed to atelier updates. Manage preferences via the link in any of our newsletters.
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {modalOpen && (
        <AddressFormModal
          initial={editingAddress}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
      <MessageModal
  open={cancelModalOpen}
  title="Cancel Order"
  message={`Are you sure you want to cancel order ${cancelOrderId}?`}
  inputLabel="Reason for cancellation"
  inputPlaceholder="Tell us why you want to cancel this order..."
  inputValue={cancelReason}
  onInputChange={setCancelReason}
  confirmText="Confirm Cancellation"
  cancelText="Keep Order"
  onConfirm={confirmCancelOrder}
  onClose={() => {
    if (cancelLoading) return;
    setCancelModalOpen(false);
    setCancelOrderId(null);
  }}
  loading={cancelLoading}
  type="default"
/>

<ReplacementModal
  open={replacementModalOpen}
  order={replacementOrder}
  itemIndex={replacementItemIndex}
  setItemIndex={setReplacementItemIndex}
  quantity={replacementQuantity}
  setQuantity={setReplacementQuantity}
  reason={replacementReason}
  setReason={setReplacementReason}
  loading={replacementLoading}
  onConfirm={submitReplacementRequest}
  onClose={() => {
    if (replacementLoading) return;

    setReplacementModalOpen(false);
    setReplacementOrder(null);
  }}
/>
    </div>
  );
}